import React from "react";
import Header from "../../components/header";
import { useLocale } from "../../localization";
import LegalGerman from "../../localization/LegalGerman";

const TermsOfService = () => {
  const { language } = useLocale();
  if (language === "de") return <LegalGerman kind="terms" />;
  return (
    <>
      <Header></Header>
      <div className="max-w-4xl mx-auto px-4 py-10 text-gray-800">
        <h1 className="text-3xl font-bold mb-6">Terms of Service ("Terms")</h1>
        <p className="text-sm text-gray-500 mb-10">
          Effective date: April 17, 2024
        </p>

        <Section title="1. Overview">
          <p>
            BookishBoyfriend ("BB", "we", "us", "our") is an interactive reading
            and AI chat platform owned and operated by{" "}
            <strong>McAnders Publishing LLC.</strong>
          </p>
        </Section>

        <Section title="2. Eligibility & Accounts">
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>
              You must be 13+ (US) / 16+ (EEA/UK) or have verifiable parental
              consent.
            </li>
            <li>
              {" "}
              Provide accurate information and keep your credentials secure. You
              are responsible for all activity under your account.
            </li>
          </ul>
        </Section>

        <Section title="3. Subscriptions, Trials & Payments">
          <ul className="list-disc pl-5 space-y-2">
            <li>
              Certain features (e.g., premium reading, extended chat tokens)
              require a paid subscription (“Premium”). Subscription terms,
              renewal dates, and pricing are disclosed at checkout.
            </li>
            <li>
              Free trials convert to paid plans unless cancelled before the end
              of the trial period.
            </li>
            <li>
              Fees are processed via our payment processor; we do not store full
              card details.
            </li>
            <li>Except as required by law, payments are nonrefundable.</li>
          </ul>
        </Section>

        <Section title="4. Intellectual Property">
          <p>
            <strong>Our IP.</strong> Site code, original text, artwork, and
            proprietary AI models are owned by BB or its licensors and are
            protected by US, UK, and EU law.
          </p>
          <p className="mt-2">
            <strong>Your IP.</strong> You retain all rights to content you write
            or upload ("User Content"). By posting or chatting you grant BB a
            worldwide, nonexclusive, royalty-free licence to store, display,
            modify, and use your User Content to operate and improve the
            Service.
          </p>
        </Section>

        <Section title="5. User Content & AI Chats">
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li>
              {" "}
              You are solely responsible for User Content and for ensuring you
              have the necessary rights to post it.
            </li>
            <li>
              AI responses are generated content provided "as is". They may be
              fictional, inaccurate, or offensive. Report any policy violations
              to{" "}
              <a
                href="mailto:support@bookishboyfriend.com"
                className="text-blue-600 underline"
              >
                support@bookishboyfriend.com
              </a>
              .
            </li>
          </ul>
          <p></p>
        </Section>

        <Section title="6. Acceptable Use">
          <p>
            You may <strong>not</strong> use BB to:
          </p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Violate any applicable law or regulation.</li>
            <li>
              Generate or distribute content that is illegal, exploitative,
              hateful, or that infringes others’ rights (see full Content
              Policy).
            </li>
            <li>Harass or abuse other users or staff.</li>
            <li>
              Reverse-engineer, scrape, or interfere with the Service or its
              security.
            </li>
          </ul>
        </Section>

        <Section title="7. Privacy & Data Protection">
          <p>
            Our practices are described in the{" "}
            <a
              href="/privacy"
              className="text-blue-600 hover:text-blue-800 underline"
            >
              Privacy Policy
            </a>{" "}
            and incorporated into these Terms. When you use BB you consent to
            those practices.
          </p>
        </Section>

        <Section title="8. Termination & Suspension">
          <p>
            We may suspend or terminate your access at any time for violation of
            these Terms or to protect the Service.
          </p>
        </Section>

        <Section title="9. Disclaimers">
          <p>
            The Service is provided <strong>"AS IS"</strong> and{" "}
            <strong>"AS AVAILABLE"</strong> without warranties of any kind. To
            the maximum extent permitted by law we disclaim all implied
            warranties, including merchantability, fitness for a particular
            purpose, and noninfringement.
          </p>
        </Section>

        <Section title="10. Limitation of Liability">
          <p>
            To the fullest extent permitted by law, BB (and its affiliates,
            officers, employees, and suppliers) will not be liable for indirect,
            special, incidental, consequential, or punitive damages, or any loss
            of profits or revenues, arising from or related to the Service.
          </p>
        </Section>

        <Section title="11. Governing Law & Dispute Resolution">
          <p>
            <strong>US/Rest of World (except UK/EEA).</strong> These Terms are
            governed by the laws of the State of California, USA, without regard
            to conflict-of-law principles. Any dispute will be resolved by
            binding arbitration under the AAA Consumer Rules in Los Angeles,
            unless you opt out within 30 days of account creation.
          </p>
          <p className="mt-2">
            <strong>UK & EEA Consumers.</strong> You may bring proceedings in
            your local courts. Mandatory consumer protection laws of your
            country of residence apply. Nothing limits statutory rights to seek
            redress through the European Online Dispute Resolution platform.
          </p>
        </Section>

        <Section title="12. Changes to Terms">
          <p>
            We may update these Terms. We will post the revised Terms and update
            the “Effective date.” Continued use after changes means acceptance.
          </p>
        </Section>

        <Section title="13. Contact">
          <p>
            <a
              href="mailto:questions@bookishboyfriend.com"
              className="text-blue-600 underline"
            >
              questions@bookishboyfriend.com
            </a>{" "}
            | McAnders Publishing LLC, Encino, California, USA
          </p>
        </Section>

        <footer className="text-xs text-gray-400 mt-10">
          &copy; {new Date().getFullYear()} McAnders Publishing LLC. All rights
          reserved.
        </footer>
      </div>
    </>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <section className="mb-8">
    <h2 className="text-xl font-semibold mb-2">{title}</h2>
    <div className="space-y-2 text-base">{children}</div>
  </section>
);

export default TermsOfService;
